import { type CandidateStatEntry } from "@crowdcoursing/api/openapi";
import { DateTime } from "luxon";

/**
 * Aggregates candidate stats by calendar day, summing metrics for entries with the same date.
 * Needed when the API returns multiple rows per day (e.g. district + child school bubbles with isAggregate).
 */
export const aggregateCandidateStatsByDate = (stats: CandidateStatEntry[] | null | undefined): CandidateStatEntry[] => {
  if (!stats || stats.length === 0) {
    return [];
  }

  const statsByDate = new Map<string, CandidateStatEntry>();

  stats.forEach((stat) => {
    if (!stat.timestamp) {
      return;
    }

    const date = DateTime.fromISO(stat.timestamp).toFormat("yyyy-MM-dd");
    const existing = statsByDate.get(date);

    if (existing && existing.metrics && stat.metrics) {
      existing.metrics = {
        studentsPlaced: (existing.metrics.studentsPlaced || 0) + (stat.metrics.studentsPlaced || 0),
        studentsWithInterest: (existing.metrics.studentsWithInterest || 0) + (stat.metrics.studentsWithInterest || 0),
        studentsWithInterviewRequests:
          (existing.metrics.studentsWithInterviewRequests || 0) + (stat.metrics.studentsWithInterviewRequests || 0),
        studentsWithInterviewsAttended:
          (existing.metrics.studentsWithInterviewsAttended || 0) + (stat.metrics.studentsWithInterviewsAttended || 0),
        studentsWithResume: (existing.metrics.studentsWithResume || 0) + (stat.metrics.studentsWithResume || 0),
        totalStudents: (existing.metrics.totalStudents || 0) + (stat.metrics.totalStudents || 0),
      };
    } else {
      statsByDate.set(date, {
        metrics: { ...stat.metrics },
        timestamp: stat.timestamp,
      });
    }
  });

  return Array.from(statsByDate.values()).sort((a, b) => (a.timestamp ?? "").localeCompare(b.timestamp ?? ""));
};
